/**
 * EnergyComparisonTab Component
 *
 * Comparison matrix showing all energy-saving scenarios side by side.
 * Reads from cached simulation results (battery_simulations,
 * solar_simulations) and the tariff comparison RPC.
 *
 * Each row shows: Scenario | Annual Cost | ∆ vs Fixed | Capex | Payback
 * ∆ column is green (saving) or red (more expensive).
 */

import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { supabase } from '@/supabase-client'

const DEFAULT_START = '2024-04-01'

function formatGbp(pence) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(pence / 100)
}

function formatCapex(gbp) {
  if (gbp == null) return '—'
  if (gbp === 0) return '£0'
  if (gbp >= 1000) return `£${(gbp / 1000).toFixed(1)}k`
  return `£${gbp.toFixed(0)}`
}

function formatPayback(years) {
  if (years == null) return '—'
  if (years === 0) return 'instant'
  if (years < 0) return '—'
  return `${years.toFixed(1)} yrs`
}

/**
 * Estimate capex from config. Battery prices from presets, solar ~£900/kWp
 * installed (UK 2026 C&I average). These are indicative — the table is
 * for comparing scenarios, not quoting.
 */
function estimateCapex({ batteryKwh, solarKwp }) {
  let total = 0
  // Battery: rough £/kWh by size band
  if (batteryKwh) {
    if (batteryKwh <= 15) total += 9000       // single Powerwall range
    else if (batteryKwh <= 25) total += 9500
    else if (batteryKwh <= 35) total += 12000
    else total += 14500                        // 40kWh Sungrow range
  }
  // Solar: ~£900/kWp installed (panels + inverter + mounting + DNO)
  if (solarKwp) {
    total += Math.round(solarKwp * 900)
  }
  return total || null
}

function annualise(totalPence, daysInWindow) {
  if (!daysInWindow || !totalPence) return 0
  return (totalPence / daysInWindow) * 365
}

export default function EnergyComparisonTab() {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [daysInWindow, setDaysInWindow] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setIsLoading(true)

      const endDate = new Date().toISOString().slice(0, 10)

      // Fetch all data sources in parallel
      const [tariffRes, batteryRes, solarRes] = await Promise.all([
        supabase.rpc('calculate_agile_comparison', {
          p_start: DEFAULT_START, p_end: endDate
        }),
        supabase.from('battery_simulations')
          .select('params, aggregate, computed_at')
          .order('computed_at', { ascending: false }),
        supabase.from('solar_simulations')
          .select('params, aggregate, computed_at')
          .order('computed_at', { ascending: false }),
      ])

      if (cancelled) return

      const scenarios = []

      // --- Fixed baseline + Agile from tariff comparison ---
      const tariffRows = tariffRes.data ?? []
      const totalDays = tariffRows.reduce((s, r) => s + (r.days_in_window ?? 0), 0)
      const totalFixed = tariffRows.reduce((s, r) => s + (r.fixed_total_excl_vat_pence ?? 0), 0)
      const totalAgile = tariffRows.reduce((s, r) => s + (r.agile_total_excl_vat_pence ?? 0), 0)

      setDaysInWindow(totalDays)

      scenarios.push({
        id: 'fixed',
        label: 'Fixed (current baseline)',
        annualCost: annualise(totalFixed, totalDays),
        capex: null,
      })

      scenarios.push({
        id: 'agile',
        label: 'Agile only',
        annualCost: annualise(totalAgile, totalDays),
        capex: 0,
      })

      // --- Battery-only scenarios (dedupe by capacity, take latest agile tariff) ---
      const batteryRows = batteryRes.data ?? []
      const seenBattery = new Set()
      for (const sim of batteryRows) {
        const tariff = sim.params?.tariff ?? 'agile'
        if (tariff !== 'agile') continue
        const cap = sim.params?.battery?.capacity_kwh_usable
        if (!cap || seenBattery.has(cap)) continue
        seenBattery.add(cap)

        const agg = sim.aggregate
        const simDays = (sim.params?.start_date && sim.params?.end_date)
          ? Math.round((new Date(sim.params.end_date) - new Date(sim.params.start_date)) / 86400000)
          : totalDays

        // Find a friendly name for the battery
        const ratedKwh = cap < 15 ? '13.5' : cap < 30 ? `${Math.round(cap)}` : `${Math.round(cap)}`
        const batLabel = cap <= 15 ? `Battery (${ratedKwh} kWh PW3)` : `Battery (${ratedKwh} kWh Sungrow)`

        scenarios.push({
          id: `bat-${cap}`,
          label: `Agile + ${batLabel}`,
          annualCost: annualise(agg.total_agile_battery_pence, simDays),
          capex: estimateCapex({ batteryKwh: cap }),
        })
      }

      // --- Solar scenarios from solar sims ---
      const solarRows = solarRes.data ?? []
      const seenSolar = new Set()
      for (const sim of solarRows) {
        const sp = sim.params?.solar
        if (!sp) continue
        const kWp = (sp.panel_watts * sp.num_panels) / 1000
        const bp = sim.params?.battery
        const batCap = bp?.capacity_kwh_usable ?? 0
        const key = `${kWp}-${batCap}`
        if (seenSolar.has(key)) continue
        seenSolar.add(key)

        const agg = sim.aggregate
        const simDays = (sim.params?.start_date && sim.params?.end_date)
          ? Math.round((new Date(sim.params.end_date) - new Date(sim.params.start_date)) / 86400000)
          : totalDays

        // Solar-only row (no battery)
        const solarOnlyKey = `${kWp}-0`
        if (!seenSolar.has(solarOnlyKey) && agg.total_agile_solar_pence) {
          seenSolar.add(solarOnlyKey)
          const solarAnnual = annualise(agg.total_agile_solar_pence, simDays)
          scenarios.push({
            id: `solar-${kWp}`,
            label: `Agile + Solar (${kWp} kWp)`,
            annualCost: solarAnnual,
            capex: estimateCapex({ solarKwp: kWp }),
          })
          // Grant-adjusted variant for small solar
          if (kWp <= 6) {
            scenarios.push({
              id: `solar-${kWp}-grant`,
              label: `Agile + Solar (${kWp} kWp) with grant`,
              annualCost: solarAnnual,
              capex: 8000,
            })
          }
        }

        // Solar + battery row
        if (batCap > 0 && agg.total_agile_solar_battery_pence) {
          const ratedKwh = batCap <= 15 ? '13.5' : `${Math.round(batCap)}`
          scenarios.push({
            id: `solar-${kWp}-bat-${batCap}`,
            label: `Agile + Solar (${kWp} kWp) + Battery (${ratedKwh} kWh)`,
            annualCost: annualise(agg.total_agile_solar_battery_pence, simDays),
            capex: estimateCapex({ solarKwp: kWp, batteryKwh: batCap }),
          })
        }
      }

      // Sort by annual cost descending (most expensive first = fixed at top)
      scenarios.sort((a, b) => b.annualCost - a.annualCost)

      setRows(scenarios)
      setIsLoading(false)
    }

    load()
    return () => { cancelled = true }
  }, [])

  const baselineCost = useMemo(() => {
    const fixed = rows.find(r => r.id === 'fixed')
    return fixed?.annualCost ?? 0
  }, [rows])

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Scenario Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scenario Comparison</CardTitle>
        <p className="text-xs text-neutral-500 mt-1">
          All figures annualised from {daysInWindow} days of data, excl VAT. Battery savings are DP ceiling (perfect foresight).
          Capex is hardware-only estimate — excludes installation, commissioning, DNO fees.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 pr-4 font-medium text-neutral-500">Scenario</th>
                <th className="text-right py-3 px-4 font-medium text-neutral-500">Annual Cost</th>
                <th className="text-right py-3 px-4 font-medium text-neutral-500">∆ vs Fixed</th>
                <th className="text-right py-3 px-4 font-medium text-neutral-500">Capex</th>
                <th className="text-right py-3 pl-4 font-medium text-neutral-500">Payback</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const delta = baselineCost - row.annualCost
                const isBaseline = row.id === 'fixed'
                const isSaving = delta > 0
                const paybackYears = row.capex && delta > 0
                  ? row.capex / (delta / 100)
                  : null

                return (
                  <tr
                    key={row.id}
                    className={`border-b border-neutral-100 ${isBaseline ? 'bg-neutral-50' : ''}`}
                  >
                    <td className="py-3 pr-4 font-medium text-neutral-900">
                      {row.label}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-neutral-900">
                      {formatGbp(row.annualCost)}
                    </td>
                    <td className={`py-3 px-4 text-right font-semibold ${
                      isBaseline ? 'text-neutral-400' : isSaving ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isBaseline
                        ? '—'
                        : `${isSaving ? '-' : '+'}${formatGbp(Math.abs(delta))}`
                      }
                    </td>
                    <td className="py-3 px-4 text-right text-neutral-600">
                      {formatCapex(row.capex)}
                    </td>
                    <td className="py-3 pl-4 text-right text-neutral-600">
                      {isBaseline ? '—' : formatPayback(paybackYears)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
